import React, { useEffect, useState, useCallback } from "react";
import { Button, DatePicker, Descriptions, Input, Modal, Pagination, Select, Table, Tag, message } from "antd";
import { endOfDay, format, fromUnixTime, getUnixTime, startOfDay } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faRotate } from "@fortawesome/free-solid-svg-icons";
import { DATE_PRESETS } from "../../../utils/date-presets";
import authApi from "../../../api/authApi";
import OrderSummaryBar from "./order-summary-bar";
import { buildOrderColumns, CHECK_STATUS_COLOR, fmt } from "./order-table-columns";
import ExportCsvButton from "../../../components/export-csv-button";
import { OrderSystemPanel, OrderClickLogPanel } from "./order-detail-panels";

const STATUS_OPTIONS  = [
    { label: "Chờ duyệt", value: 1 },
    { label: "Đã duyệt",  value: 2 },
    { label: "Đã huỷ",    value: 3 },
];

const CAMPAIGN_OPTIONS = [
    { label: "Shopee", value: "shopee" },
    { label: "Lazada", value: "lazada" },
    { label: "TikTok", value: "tiktok" },
    { label: "ShopeeFood", value: "shopeefood" },
];

const CHANNEL_OPTIONS = [
    { label: "App", value: "app" },
    { label: "Web", value: "web" },
];

const fmtDateTime = (ts) => (ts ? format(fromUnixTime(ts), "HH:mm:ss dd/MM/yyyy") : "—");

const DEFAULT_FILTER = { page: 1, limit: 20, userId: "", orderId: "", conversionStatus: undefined, campId: undefined, channel: undefined, startDate: undefined, endDate: undefined };

/** Prefill từ query string (?orderId=&userId=) — dùng cho link đối soát từ màn
 *  chi tiết Chương trình thưởng mở thẳng đúng đơn/đúng user. */
const initialSearchFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return { orderId: params.get("orderId") || "", userId: params.get("userId") || "" };
};

function Order() {
    const [orders, setOrders]       = useState([]);
    const [totalOrder, setTotalOrder] = useState(0);
    const [orderStats, setOrderStats] = useState({ total: 0, pending: 0, completed: 0, cancelled: 0 });
    const [loading, setLoading]     = useState(false);
    const [filter, setFilter]       = useState(() => ({ ...DEFAULT_FILTER, ...initialSearchFromUrl() }));
    // Ô nhập text là state nháp: chỉ áp dụng khi bấm "Tìm kiếm" / Enter (tránh tự search lúc đang gõ)
    const [search, setSearch]       = useState(initialSearchFromUrl);
    const [checkingIds, setCheckingIds] = useState(() => new Set());
    const [bulkChecking, setBulkChecking] = useState(false);
    const [checkResult, setCheckResult] = useState(null); // { order, rows, checkedAt }
    const [checkedStatus, setCheckedStatus] = useState({}); // { [orderId]: "CONVERSION_STATUS_*" } — trạng thái Shopee thật sau khi check
    const [actionLoading, setActionLoading] = useState(false);

    /** Chỉ phần điều kiện lọc (không phân trang) — dùng chung cho danh sách và
     *  nút Xuất CSV, để file tải về khớp đúng bảng đang hiển thị. */
    const buildFilterQuery = useCallback((f) => {
        const parts = [];
        if (f.orderId) parts.push(`orderId=${f.orderId}`);
        if (f.userId)  parts.push(`userId=${f.userId}`);
        if (f.conversionStatus) parts.push(`conversionStatus=${f.conversionStatus}`);
        if (f.campId)  parts.push(`campId=${f.campId}`);
        if (f.channel) parts.push(`channel=${f.channel}`);
        if (f.startDate && f.endDate) {
            parts.push(`clickTime[gte]=${f.startDate}`);
            parts.push(`clickTime[lte]=${f.endDate}`);
        }
        return parts;
    }, []);

    /** Build query string from flat filter object */
    const buildQuery = useCallback(
        (f) => ["sort=-clickTime", `page=${f.page}`, `limit=${f.limit}`, ...buildFilterQuery(f)].join("&"),
        [buildFilterQuery]
    );

    const fetchOrders = useCallback(async (f) => {
        setLoading(true);
        try {
            const { data } = await authApi.getAllOrder(buildQuery(f));
            setOrders(data.allOrder);
            setTotalOrder(data.totalOrder);
            if (data.stats) setOrderStats(data.stats);
        } catch {
            message.error("Không thể tải danh sách đơn hàng");
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Filter đã "chốt" (select/date/pagination/nút Tìm kiếm) → fetch ngay, không debounce
    useEffect(() => { fetchOrders(filter); }, [filter, fetchOrders]);

    const setField = (key, value) => setFilter((p) => ({ ...p, [key]: value, page: 1 }));

    /** Áp dụng ô nhập text vào filter (bấm nút Tìm kiếm hoặc Enter). Nhận override để clear tức thì. */
    const applySearch = (override) => {
        const next = { ...search, ...override };
        setSearch(next);
        setFilter((p) => ({ ...p, orderId: next.orderId.trim(), userId: next.userId.trim(), page: 1 }));
    };

    /** Gõ vào ô text: chỉ cập nhật nháp; nếu xoá trắng (nút clear) thì áp dụng luôn. */
    const onSearchChange = (key, value) => {
        if (!value) applySearch({ [key]: "" });
        else setSearch((p) => ({ ...p, [key]: value }));
    };

    const handleDateChange = (value) => {
        if (!value) {
            setFilter((p) => ({ ...p, startDate: undefined, endDate: undefined, page: 1 }));
        } else {
            setFilter((p) => ({
                ...p,
                startDate: getUnixTime(startOfDay(value[0]["$d"])),
                endDate:   getUnixTime(endOfDay(value[1]["$d"])),
                page: 1,
            }));
        }
    };

    const confirmComplete = (id) =>
        Modal.confirm({
            title: "Xác nhận hoàn thành đơn hàng",
            content: <span>Đánh dấu đơn <strong>{id}</strong> là <strong>ĐÃ HOÀN THÀNH</strong>?</span>,
            okText: "Xác nhận",
            cancelText: "Hủy",
            onOk: async () => {
                await authApi.setOrderComplete(id);
                message.success("Đã cập nhật đơn hàng");
                fetchOrders(filter);
            },
        });

    /** Check 1 đơn: gọi Shopee, merge trạng thái mới vào row. Trả về data (hoặc null nếu lỗi). */
    const checkOne = async (id) => {
        setCheckingIds((p) => new Set(p).add(id));
        try {
            const { data } = await authApi.checkOrder(id);
            setOrders((prev) => prev.map((o) => (o.orderId === id ? { ...o, ...data.order } : o)));
            // Ghi nhớ trạng thái Shopee thật (từ rows) để hiện badge cạnh nút Check → đối chiếu với data đang hiển thị
            const shopeeStatus = data.rows?.[0]?.status || null;
            if (shopeeStatus) setCheckedStatus((p) => ({ ...p, [id]: shopeeStatus }));
            return data;
        } catch (e) {
            message.error(e?.response?.data?.message || `Không check được đơn ${id}`);
            return null;
        } finally {
            setCheckingIds((p) => { const n = new Set(p); n.delete(id); return n; });
        }
    };

    /** Check 1 đơn + mở popup chi tiết. */
    const handleCheckOne = async (id) => {
        const data = await checkOne(id);
        if (data) setCheckResult({ ...data, checkedAt: getUnixTime(new Date()) });
    };

    /** Check hàng loạt: lần lượt từng đơn Shopee, KHÔNG bung popup. */
    const handleBulkCheck = async () => {
        const targets = orders.filter((o) => (o.campId || "shopee") === "shopee");
        if (!targets.length || bulkChecking) {
            if (!targets.length) message.info("Không có đơn Shopee để check");
            return;
        }
        setBulkChecking(true);
        const hide = message.loading(`Đang check ${targets.length} đơn Shopee...`, 0);
        let ok = 0;
        for (const o of targets) {
            // eslint-disable-next-line no-await-in-loop
            if (await checkOne(o.orderId)) ok++;
        }
        hide();
        setBulkChecking(false);
        message.success(`Check xong: ${ok}/${targets.length} đơn cập nhật`);
    };

    const closeCheck = () => setCheckResult(null);

    /** Duyệt đơn từ popup (đơn chưa trả tiền). */
    const approveFromPopup = async () => {
        const id = checkResult?.order?.orderId;
        if (!id) return;
        setActionLoading(true);
        try {
            await authApi.setOrderComplete(id);
            message.success("Đã duyệt đơn");
            closeCheck();
            fetchOrders(filter);
        } catch (e) {
            message.error(e?.response?.data?.message || "Duyệt đơn thất bại");
        } finally {
            setActionLoading(false);
        }
    };

    /** Thu hồi hoa hồng từ popup (đơn đã trả tiền). */
    const revokeFromPopup = () => {
        const id = checkResult?.order?.orderId;
        if (!id) return;
        Modal.confirm({
            title: "Thu hồi hoa hồng",
            content: <span>Trừ lại hoa hồng đơn <strong>{id}</strong> khỏi user và đánh dấu <strong>ĐÃ HUỶ</strong>?</span>,
            okText: "Thu hồi",
            okButtonProps: { danger: true },
            cancelText: "Huỷ",
            onOk: async () => {
                try {
                    await authApi.revokeOrderCommission(id);
                    message.success("Đã thu hồi hoa hồng");
                    closeCheck();
                    fetchOrders(filter);
                } catch (e) {
                    message.error(e?.response?.data?.message || "Thu hồi thất bại");
                    throw e;
                }
            },
        });
    };

    const dataSource = orders.map((el, i) => ({
        key: i,
        id: el.orderId, status: el.conversionStatus,
        clickTime: el.clickTime, payTime: el.payTime,
        // purchaseTime = giờ sàn ghi nhận đơn (app gọi "Thời gian đặt hàng"), completeTime = giờ
        // sàn báo hoàn thành. NV check đơn cần 2 mốc này: điều kiện đợt thưởng và thứ tự chia
        // ngân sách đều tính theo purchaseTime, KHÔNG phải clickTime của cột "Click".
        purchaseTime: el.purchaseTime ?? null, completeTime: el.completeTime ?? null,
        userId: el.userId, payedToUser: el.payedToUser,
        campId: el.campId || "—", orderStatus: el.orderStatus,
        cookiePlatform: el.cookiePlatform ?? null,
        commission: el.commission, userCommission: el.userCommission,
        refId: el.refId ?? null, refCommission: el.refCommission ?? 0,
        channel: el.channel ?? null, appVersion: el.appVersion ?? null, os: el.os ?? null,
        x2: el.x2 ?? false, x2Multiplier: el.x2Multiplier ?? 1,
        reward: el.reward ?? null,
    }));

    const columns = buildOrderColumns({ applySearch, checkedStatus, checkingIds, handleCheckOne, confirmComplete });

    // Danh sách dọc (nhãn : giá trị) cho popup chi tiết — mỗi sản phẩm 1 block Descriptions
    const popupItems = (r) => [
        { key: "purchaseTime", label: "Thời gian", children: fmtDateTime(r.purchaseTime) },
        { key: "orderSn", label: "Mã đơn hàng", children: r.orderSn },
        { key: "status", label: "Trạng thái", children: <Tag color={CHECK_STATUS_COLOR[r.status] || "default"}>{r.status}</Tag> },
        { key: "orderValue", label: "Giá trị đơn hàng", children: fmt(r.orderValue) },
        { key: "commission", label: "Hoa hồng", children: fmt(r.commission) },
        { key: "itemId", label: "Mã sản phẩm", children: r.itemId },
        { key: "qty", label: "Số lượng", children: r.qty ?? "—" },
        // Shopee tách 1 sản phẩm thành nhiều dòng khi một phần số lượng ăn khuyến mãi →
        // 2 block cùng "Mã sản phẩm", chỉ mã khuyến mãi phân biệt được.
        { key: "promotionId", label: "Mã khuyến mãi", children: r.promotionId || "—" },
        { key: "cancelReason", label: "Lý do hủy", children: r.cancelReason || "—" },
        { key: "updatedAt", label: "Cập nhật", children: fmtDateTime(checkResult?.checkedAt) },
    ];

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-3">
            <h1 className="text-xl font-bold">Danh sách đơn hàng</h1>

            {/* Filter bar */}
            <div className="bg-surface border border-line rounded-lg p-3 flex flex-wrap gap-2 items-center shadow-sm">
                <Input
                    placeholder="Mã đơn hàng"
                    value={search.orderId}
                    onChange={(e) => onSearchChange("orderId", e.target.value)}
                    onPressEnter={() => applySearch()}
                    style={{ width: 170 }}
                    allowClear
                />
                <Input
                    placeholder="User ID"
                    value={search.userId}
                    onChange={(e) => onSearchChange("userId", e.target.value)}
                    onPressEnter={() => applySearch()}
                    style={{ width: 110 }}
                    allowClear
                />
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                    loading={loading}
                    onClick={() => applySearch()}
                >
                    Tìm kiếm
                </Button>
                <Select
                    placeholder="Trạng thái"
                    allowClear
                    style={{ width: 130 }}
                    options={STATUS_OPTIONS}
                    onChange={(v) => setField("conversionStatus", v)}
                />
                <Select
                    placeholder="Nguồn"
                    allowClear
                    style={{ width: 110 }}
                    options={CAMPAIGN_OPTIONS}
                    onChange={(v) => setField("campId", v)}
                />
                <Select
                    placeholder="Kênh"
                    allowClear
                    style={{ width: 100 }}
                    options={CHANNEL_OPTIONS}
                    onChange={(v) => setField("channel", v)}
                />
                <DatePicker.RangePicker
                    onChange={handleDateChange}
                    placeholder={["Từ ngày", "Đến ngày"]}
                    format="DD/MM/YYYY"
                    style={{ width: 230 }}
                    presets={DATE_PRESETS}
                />
                <ExportCsvButton
                    className="ml-auto"
                    filename="don-hang"
                    disabled={!totalOrder}
                    tooltip="Tải toàn bộ đơn đang lọc ra CSV (mọi trang) để tự cộng tổng đơn hoàn / huỷ trong Excel và đối chiếu với số phía trên"
                    request={() => authApi.exportOrders(buildFilterQuery(filter).join("&"))}
                />
                <Button
                    type="primary"
                    ghost
                    icon={<FontAwesomeIcon icon={faRotate} />}
                    loading={bulkChecking}
                    disabled={!orders.length}
                    onClick={handleBulkCheck}
                >
                    Check hàng loạt
                </Button>
            </div>

            <OrderSummaryBar stats={orderStats} />

            <Table
                size="small"
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                loading={loading}
                scroll={{ x: "max-content" }}
                rowClassName={() => "whitespace-nowrap"}
            />

            <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-sm text-content-3">Tổng: {totalOrder.toLocaleString()} đơn hàng</span>
                <Pagination
                    total={totalOrder}
                    current={filter.page}
                    pageSize={filter.limit}
                    showSizeChanger
                    pageSizeOptions={[20, 50, 100, 200, 500]}
                    onChange={(page, limit) => setFilter((p) => ({ ...p, page, limit }))}
                />
            </div>

            {/* Popup chi tiết khi Check 1 đơn */}
            <Modal
                open={!!checkResult}
                onCancel={closeCheck}
                title={checkResult ? `Chi tiết đơn ${checkResult.order.orderId}` : ""}
                width={860}
                footer={
                    checkResult ? (() => {
                        // Nút hành động tùy theo ĐỐI CHIẾU: trạng thái Shopee thật (check) vs trạng thái hệ thống (DB).
                        const checkRaw = checkResult.rows?.[0]?.status; // CONVERSION_STATUS_*
                        const isCheckComplete = checkRaw === "CONVERSION_STATUS_COMPLETE";
                        const isCheckCancel = checkRaw === "CONVERSION_STATUS_CANCEL";
                        const { payedToUser } = checkResult.order;
                        // Shopee HOÀN THÀNH mà đơn CHƯA trả tiền → luôn cho Duyệt (trả hoa hồng),
                        // giống cơ chế nút "Duyệt" ngoài list, không phụ thuộc trạng thái DB đang chờ hay đã duyệt.
                        const canApprove = isCheckComplete && !payedToUser;
                        // Shopee HUỶ mà đơn ĐÃ trả tiền → phải Huỷ + thu hồi điểm + ghi log (đơn chưa trả đã tự huỷ khi check).
                        const canRevoke = isCheckCancel && payedToUser;
                        return (
                            <div className="flex justify-end gap-2">
                                {canApprove && (
                                    <Button type="primary" loading={actionLoading} onClick={approveFromPopup}>Duyệt đơn</Button>
                                )}
                                {canRevoke && (
                                    <Button danger onClick={revokeFromPopup}>Huỷ đơn &amp; thu hồi điểm</Button>
                                )}
                                <Button onClick={closeCheck}>Đóng</Button>
                            </div>
                        );
                    })() : null
                }
            >
                {checkResult && (
                    <>
                        <OrderSystemPanel order={checkResult.order} />
                        <OrderClickLogPanel order={checkResult.order} clickLog={checkResult.clickLog} />
                        {/* Dữ liệu sàn trả về lúc bấm Check — mỗi sản phẩm 1 block */}
                        <div className="mt-4 font-semibold">Dữ liệu Shopee (lúc check)</div>
                        {(checkResult.rows || []).map((r, i) => (
                            <Descriptions
                                key={i}
                                bordered
                                size="small"
                                column={2}
                                items={popupItems(r)}
                                style={{ marginTop: 8 }}
                            />
                        ))}
                    </>
                )}
            </Modal>
        </div>
    );
}

export default Order;
